import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
  Collapse
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { resultsService } from '../api/resultsService';

const Row = ({ result, onDelete, onSaveActualAnswer }) => {
  const [open, setOpen] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState(false);
  const [editValue, setEditValue] = useState(result.actualCorrectAnswer || '');

  const handleSave = () => {
    onSaveActualAnswer(result.id, editValue);
    setEditingAnswer(false);
  };

  const handleCancel = () => {
    setEditValue(result.actualCorrectAnswer || '');
    setEditingAnswer(false);
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{result.questionNumber}</TableCell>
        <TableCell>{result.subject}</TableCell>
        <TableCell>{result.question}</TableCell>
        <TableCell>{result.geminiAnswer || '-'}</TableCell>
        <TableCell>{result.anthropicAnswer || '-'}</TableCell>
        <TableCell>{result.openaiAnswer || '-'}</TableCell>
        <TableCell>
          {editingAnswer ? (
            <FormControl size="small">
              <Select
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
              >
                <MenuItem value="A">A</MenuItem>
                <MenuItem value="B">B</MenuItem>
                <MenuItem value="C">C</MenuItem>
                <MenuItem value="D">D</MenuItem>
              </Select>
            </FormControl>
          ) : (
            result.actualCorrectAnswer || '-'
          )}
        </TableCell>
        <TableCell>
          {editingAnswer ? (
            <Box>
              <Button 
                size="small" 
                variant="contained" 
                onClick={handleSave}
                sx={{ mr: 1 }}
              >
                Save
              </Button>
              <Button 
                size="small" 
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex' }}>
              <Button 
                size="small" 
                onClick={() => setEditingAnswer(true)}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              <Tooltip title="Delete">
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={() => onDelete(result.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Solutions
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>AI Model</TableCell>
                    <TableCell>Solution/Explanation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.geminiSolution && (
                    <TableRow>
                      <TableCell component="th" scope="row">Gemini</TableCell>
                      <TableCell style={{ whiteSpace: 'pre-wrap' }}>{result.geminiSolution}</TableCell>
                    </TableRow>
                  )}
                  {result.anthropicSolution && (
                    <TableRow>
                      <TableCell component="th" scope="row">Anthropic</TableCell>
                      <TableCell style={{ whiteSpace: 'pre-wrap' }}>{result.anthropicSolution}</TableCell>
                    </TableRow>
                  )}
                  {result.openaiSolution && (
                    <TableRow>
                      <TableCell component="th" scope="row">OpenAI</TableCell>
                      <TableCell style={{ whiteSpace: 'pre-wrap' }}>{result.openaiSolution}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const SolutionsTable = () => {
  const [results, setResults] = useState([]);

  useEffect(() => {
    loadResults();
  }, []);

  const loadResults = () => {
    const sortedResults = resultsService.getAllResults();
    setResults(sortedResults);
  };

  const handleExportCSV = () => {
    const headers = [
      'Question No.', 'Subject', 'Question', 
      'Option A', 'Option B', 'Option C', 'Option D',
      'Gemini Answer', 'Gemini Solution',
      'Anthropic Answer', 'Anthropic Solution',
      'OpenAI Answer', 'OpenAI Solution',
      'Actual Correct Answer'
    ];
    
    const csvContent = results.map(result => [
      result.questionNumber,
      result.subject,
      result.question,
      result.options?.A || '-',
      result.options?.B || '-',
      result.options?.C || '-',
      result.options?.D || '-',
      result.geminiAnswer || '-',
      result.geminiSolution || '-',
      result.anthropicAnswer || '-',
      result.anthropicSolution || '-',
      result.openaiAnswer || '-',
      result.openaiSolution || '-',
      result.actualCorrectAnswer || '-'
    ]);

    const csvData = [headers, ...csvContent];
    const csvString = csvData
      .map(row => row
        .map(cell => {
          const cellStr = String(cell || '');
          const escaped = cellStr.replace(/"/g, '""');
          return cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n') 
            ? `"${escaped}"` 
            : escaped;
        })
        .join(',')
      )
      .join('\n');

    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `neet_solutions_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveActualAnswer = (questionId, value) => {
    resultsService.updateActualAnswer(questionId, value);
    loadResults();
  };

  const handleDeleteResult = (resultId) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      resultsService.deleteResult(resultId);
      loadResults();
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all results? This cannot be undone.')) {
      resultsService.clearAllResults();
      loadResults();
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          AI Model Solutions Comparison
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {results.length > 0 && (
            <>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={handleExportCSV}
                startIcon={<DownloadIcon />}
              >
                Export CSV
              </Button>
              <Button 
                variant="outlined" 
                color="error" 
                onClick={handleClearAll}
              >
                Clear All Results
              </Button>
            </>
          )}
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="AI solutions comparison table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Q. No</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Question</TableCell>
              <TableCell>Gemini's Answer</TableCell>
              <TableCell>Anthropic's Answer</TableCell>
              <TableCell>OpenAI's Answer</TableCell>
              <TableCell>Actual Correct Answer</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No data available. Process questions to populate this table.
                </TableCell>
              </TableRow>
            ) : (
              results.map((result) => (
                <Row 
                  key={result.id} 
                  result={result}
                  onDelete={handleDeleteResult}
                  onSaveActualAnswer={handleSaveActualAnswer}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SolutionsTable;